import React, { useCallback, useEffect, useState } from 'react';
import type { AppContext } from '../../app';
import type { Shell, ShellPersona } from '../../shells/types';
import { getMascotManager } from '../../moderation/MascotManager';
import { useShell } from './ShellProvider';

const PERSONA_ORDER: ShellPersona[] = ['adult', 'kids', 'teens'];

const PERSONA_LABEL: Record<ShellPersona, string> = {
  adult: 'Adult',
  kids: 'Kids',
  teens: 'Teens',
};

/**
 * Shell picker surface body. Lists every registered shell grouped by
 * persona. Tapping a shell activates it (after PIN gate if needed).
 *
 * Kid-shell semantics (interim, until Phase 71 full decouples):
 *   - Switching INTO a kid shell asks for a PIN, enables kid-mode.
 *   - Switching OUT of a kid shell asks for the SAME PIN, disables
 *     kid-mode and lets the new shell take over.
 */
export function ShellPicker({ ctx }: { ctx: AppContext }): JSX.Element {
  const { shell, allShells, setShell } = useShell();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pinPrompt, setPinPrompt] = useState<{ target: Shell; mode: 'enter' | 'exit' } | null>(null);
  const [pin, setPin] = useState('');

  useEffect(() => { setError(null); }, [pinPrompt]);

  const onActivate = useCallback(async (target: Shell) => {
    if (target.id === shell.id) return;
    setError(null);

    const leavingKidLock = !!shell.kidLock;
    const enteringKidLock = !!target.kidLock;

    if (leavingKidLock || enteringKidLock) {
      setPinPrompt({ target, mode: leavingKidLock ? 'exit' : 'enter' });
      return;
    }

    setBusy(true);
    try { await setShell(target.id); }
    catch (e: unknown) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }, [shell.id, shell.kidLock, setShell]);

  const onSubmitPin = useCallback(async () => {
    if (!pinPrompt) return;
    setBusy(true);
    setError(null);
    try {
      const mm = getMascotManager(ctx);
      if (pinPrompt.mode === 'exit') {
        const ok = await mm.disableKidMode(pin);
        if (!ok) { setError('PIN did not match.'); return; }
      } else {
        await mm.enableKidMode(pin);
      }
      await setShell(pinPrompt.target.id);
      setPinPrompt(null);
      setPin('');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [pinPrompt, pin, ctx, setShell]);

  const grouped = PERSONA_ORDER.map((p) => ({
    persona: p,
    items: allShells.filter((s) => s.persona === p),
  })).filter((g) => g.items.length > 0);

  return (
    <div data-testid="shell-picker" style={{ padding: '4px 0' }}>
      <div className="t-meta dim" style={{ marginBottom: 12 }}>
        Choose how ALDS looks + behaves. All shells use the same surfaces underneath; switching is non-destructive.
      </div>

      {grouped.map((g) => (
        <div key={g.persona} style={{ marginBottom: 18 }}>
          <div className="t-meta env-sublabel" style={{ marginBottom: 8 }}>{PERSONA_LABEL[g.persona]}</div>
          {g.items.map((s) => {
            const active = s.id === shell.id;
            return (
              <div
                key={s.id}
                role="button"
                tabIndex={0}
                aria-pressed={active}
                data-testid={`shell-tile-${s.id}`}
                className="more-row"
                style={active ? { borderColor: 'var(--phos)', borderStyle: 'solid' } : undefined}
                onClick={() => { void onActivate(s); }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') void onActivate(s); }}
              >
                <div className="more-row-body">
                  <div className="more-row-title">
                    {s.label}{s.kidLock ? ' · 🔒' : ''}{active ? ' · active' : ''}
                  </div>
                  <div className="more-row-desc">{s.blurb}</div>
                </div>
                <div className="more-row-arrow">›</div>
              </div>
            );
          })}
        </div>
      ))}

      {pinPrompt && (
        <div data-testid="shell-pin-prompt" style={{ marginTop: 16, padding: 12, border: '1px solid var(--ink-dim)' }}>
          <div className="t-meta" style={{ marginBottom: 8 }}>
            {pinPrompt.mode === 'enter'
              ? `Set a PIN to lock "${pinPrompt.target.label}". You'll need it to switch back out.`
              : `Enter the kid-mode PIN to switch to "${pinPrompt.target.label}".`}
          </div>
          <input
            type="password"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            placeholder="PIN (4+ chars)"
            data-testid="shell-pin-input"
            style={{ width: '100%', marginBottom: 8 }}
            autoFocus
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              data-testid="shell-pin-submit"
              disabled={busy || pin.length < 4}
              onClick={() => { void onSubmitPin(); }}
            >
              {pinPrompt.mode === 'enter' ? 'Enable + switch' : 'Verify + switch'}
            </button>
            <button
              type="button"
              data-testid="shell-pin-cancel"
              disabled={busy}
              onClick={() => { setPinPrompt(null); setPin(''); }}
            >
              Cancel
            </button>
          </div>
          {error && (
            <div data-testid="shell-pin-error" className="t-meta" style={{ color: 'var(--warn)', marginTop: 8 }}>
              {error}
            </div>
          )}
        </div>
      )}

      {error && !pinPrompt && (
        <div data-testid="shell-error" className="t-meta" style={{ color: 'var(--warn)', marginTop: 8 }}>
          {error}
        </div>
      )}
    </div>
  );
}
